/**
 * Gates a canvas node's media behind the bounded canvas image loader.
 *
 * See `canvasImageLoader.ts` for why: full-resolution assets + ReactFlow
 * mounting every in-viewport node at once means a zoomed-out dense canvas
 * fires hundreds of concurrent requests and paints top-down. This hook makes a
 * card wait its turn, ordered by how close it is to the viewport centre.
 *
 * Three properties matter as much as the throttling itself:
 *
 *  - When the pool has spare capacity the card is admitted on its FIRST render,
 *    before paint. Nothing is deferred on a sparse canvas.
 *  - While a NEW url loads, the previously admitted url keeps being returned,
 *    so switching variants or finishing a regeneration swaps the `<img>` src
 *    in place instead of blanking the card and remounting the element.
 *  - A slot is released the moment this hook stops being responsible for it —
 *    on settle, on url change, on `enabled` going false, and on unmount. A
 *    leaked slot is worse than the original bug: eight of them exhaust the
 *    pool and every remaining card stays blank.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import {
  acquireCanvasImageSlot,
  releaseCanvasImageSlot,
  tryAdmitCanvasImageNow,
} from '../../canvasImageLoader'

export type CanvasNodeImage = {
  /** The url safe to render right now, or null to show a placeholder. */
  displayUrl: string | null
  /** Wire to the `<img>`/`<video>` `onLoad` AND `onError`; frees the slot. */
  onMediaSettled: () => void
}

export type UseCanvasNodeImageOptions = {
  /**
   * Set false when the caller will NOT render the gated `<img>` this pass —
   * e.g. a spinner branch, or a lightweight-chrome early return that draws its
   * own media. Such a branch can never fire `onMediaSettled`, so reserving a
   * slot for it strands one permanently.
   *
   * Defaults to true. The loader's watchdog is the backstop for branches that
   * forget; this flag is the precise fix that avoids a 15s stall.
   */
  enabled?: boolean
}

/**
 * @param url        Asset URL, or null when the node has no media yet.
 * @param elementRef Card element, used to measure distance from the viewport
 *                   centre. Priority falls back to 0 (admit first) when the
 *                   element is not measurable yet.
 */
export function useCanvasNodeImage(
  url: string | null | undefined,
  elementRef: React.RefObject<HTMLElement | null>,
  options?: UseCanvasNodeImageOptions,
): CanvasNodeImage {
  const enabled = options?.enabled ?? true

  const [displayUrl, setDisplayUrl] = useState<string | null>(null)
  /**
   * The url this hook currently holds a slot for, or null. Tracked separately
   * from `displayUrl` because the two legitimately diverge: while a new url is
   * queued we keep DISPLAYING the old one, but the slot we own is the new
   * one's. Releasing by `displayUrl` would leak the in-flight reservation.
   */
  const ownedUrlRef = useRef<string | null>(null)

  const releaseOwned = useCallback(() => {
    if (ownedUrlRef.current === null) return
    releaseCanvasImageSlot(ownedUrlRef.current)
    ownedUrlRef.current = null
  }, [])

  // useLayoutEffect, not useEffect: admission is resolved BEFORE paint, so an
  // uncontended canvas shows its image on the first painted frame rather than
  // flashing a placeholder.
  useLayoutEffect(() => {
    if (!url || !enabled) {
      // Covers url -> null, and a branch switching to one that renders no
      // gated <img> (spinner / lightweight chrome).
      releaseOwned()
      if (!url) setDisplayUrl(null)
      return
    }

    // A new url supersedes whatever we were holding: the old <img> is going
    // away and will never fire onLoad, so its slot must not stay reserved.
    if (ownedUrlRef.current !== null && ownedUrlRef.current !== url) releaseOwned()

    if (tryAdmitCanvasImageNow(url)) {
      ownedUrlRef.current = url
      setDisplayUrl(url)
      return
    }

    let priority = 0
    const el = elementRef.current
    if (el) {
      const rect = el.getBoundingClientRect()
      const dx = rect.left + rect.width / 2 - window.innerWidth / 2
      const dy = rect.top + rect.height / 2 - window.innerHeight / 2
      priority = Math.hypot(dx, dy)
    }
    // Previously-seen urls are promoted inside the loader, so a pan-back is
    // served first without bypassing the bound.

    let cancelled = false
    void acquireCanvasImageSlot(url, priority).then(() => {
      if (cancelled) {
        // The card went away (or moved on) before its turn came up; do not
        // strand the slot we were just granted.
        releaseCanvasImageSlot(url)
        return
      }
      ownedUrlRef.current = url
      setDisplayUrl(url)
    })

    return () => {
      cancelled = true
    }
  }, [url, enabled, elementRef, releaseOwned])

  // Release on unmount so a node panned offscreen mid-load frees its slot.
  useEffect(() => releaseOwned, [releaseOwned])

  const onMediaSettled = useCallback(() => {
    releaseOwned()
  }, [releaseOwned])

  return { displayUrl, onMediaSettled }
}
